import { landscapes, landscapesSection, type Landscape } from '@/content/site'

import { Reveal } from './shared/Reveal'
import { SplitTitle } from './shared/SplitTitle'

const GLYPHS: Record<string, string> = {
  peak: 'M3 20l6.5-11L14 16l2.5-4L21 20z',
  book: 'M4 5.5A2.5 2.5 0 016.5 3H20v15.5H6.5A2.5 2.5 0 004 21z M20 18.5H6.5',
  branch: 'M6 4v10a3 3 0 003 3h9 M6 20a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M18 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3z M18 5.5h1.5A2.5 2.5 0 0122 8v3',
  shield: 'M12 3l7 3v5.5c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6z',
  rocket: 'M12 3c3.5 1.5 5.5 5 5.5 9l-3 3h-5l-3-3C6.5 8 8.5 4.5 12 3z M9.5 15l-2 4 3-1.5M14.5 15l2 4-3-1.5',
}

const ROLE_TEXT: Record<Landscape['role'], string> = {
  source: 'text-node-source',
  agent: 'text-node-agent',
  engine: 'text-node-engine',
  graph: 'text-node-graph',
  output: 'text-node-output',
}

export function StackSection() {
  return (
    <section id="landscapes" className="py-16 md:py-24">
      <div className="shell">
        <Reveal>
          <p className="eyebrow">{landscapesSection.eyebrow}</p>
        </Reveal>

        <h2 className="max-w-2xl font-sans text-[1.6rem] md:text-[2.15rem] font-bold tracking-[-0.02em] leading-[1.16]">
          <SplitTitle text={landscapesSection.title} />
        </h2>

        <Reveal i={1}>
          <p className="mt-5 max-w-[46rem] text-[0.95rem] md:text-[1.05rem] leading-[1.75] text-ink-dim">
            {landscapesSection.intro}
          </p>
        </Reveal>

        {/* Hairline grid: the parent's background shows through 1px gap seams,
            so the cards need no borders or shadows of their own. */}
        <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-5">
          {landscapes.map((item, i) => (
            <Reveal key={item.id} i={i} className="h-full">
              {/* Summary only — domain, name, tagline. The prose paragraph and
                  the entity list were both cut: five cards of detail side by
                  side read as a wall of text. Both are still in site.ts if
                  they are wanted elsewhere. */}
              <article className="flex h-full flex-col bg-bg p-5 transition-colors hover:bg-accent/5 md:p-6">
                <div className="mb-4 flex items-start justify-between">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className={ROLE_TEXT[item.role]}
                  >
                    <path
                      d={GLYPHS[item.glyph]}
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="font-mono text-[0.63rem] tracking-[0.16em] text-accent">
                    {item.index}
                  </span>
                </div>

                <p className={`font-mono text-[0.63rem] uppercase tracking-[0.18em] ${ROLE_TEXT[item.role]}`}>
                  {item.domain}
                </p>
                <h3 className="mt-1.5 font-sans text-[1.2rem] font-bold tracking-[-0.02em] md:text-[1.4rem]">
                  {item.name}
                </h3>
                <p className="mt-2.5 font-paragraph text-[0.8rem] leading-relaxed text-ink-dim">
                  {item.tagline}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}
